/**
 * useLifecycleAnimation
 *
 * Animation state machine for the Fire Lifecycle Visualizer.
 * Controls playback of the date slider across the fire's lifespan.
 *
 * - Uses requestAnimationFrame for smooth playback
 * - Advances by speed × 1day per ~500ms frame interval
 * - Auto-stops at endDate
 * - Clamps selectedDate to [startDate, endDate]
 */
import { useState, useEffect, useRef, useCallback } from 'react';

export type AnimationSpeed = 0.5 | 1 | 2;

export interface UseLifecycleAnimationResult {
  selectedDate: string;
  playing: boolean;
  speed: AnimationSpeed;
  play: () => void;
  pause: () => void;
  stepForward: () => void;
  stepBack: () => void;
  setSpeed: (speed: AnimationSpeed) => void;
  setDate: (date: string) => void;
}

const DAY_MS = 86400000;
/** Milliseconds between animation frame advances */
const FRAME_INTERVAL_MS = 500;

/**
 * Convert epoch ms to a YYYY-MM-DD string. Returns '' for invalid/zero values.
 */
function toDateStr(ms: number): string {
  if (!ms || isNaN(ms)) return '';
  return new Date(ms).toISOString().split('T')[0];
}

/**
 * Parse a date string to epoch ms. Returns 0 for empty/null/invalid inputs.
 */
function toMs(dateStr: string | null | undefined): number {
  if (!dateStr) return 0;
  const ms = new Date(dateStr).getTime();
  return isNaN(ms) ? 0 : ms;
}

export function useLifecycleAnimation(
  startDate: string | null,
  endDate: string | null
): UseLifecycleAnimationResult {
  const startMs = toMs(startDate);
  const endMs = toMs(endDate);

  const [selectedDate, setSelectedDateState] = useState<string>(
    startDate ? toDateStr(startMs) : ''
  );
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeedState] = useState<AnimationSpeed>(1);

  // Refs for RAF loop — avoid stale closures
  const playingRef = useRef(false);
  const speedRef = useRef<AnimationSpeed>(1);
  const selectedMsRef = useRef(startMs);
  const lastFrameTimeRef = useRef<number | null>(null);
  const rafHandleRef = useRef<number | null>(null);

  // Keep refs in sync with state
  useEffect(() => { playingRef.current = playing; }, [playing]);
  useEffect(() => { speedRef.current = speed; }, [speed]);

  // Re-init selectedDate if startDate changes (e.g. hook remount)
  useEffect(() => {
    if (startDate) {
      const ms = toMs(startDate);
      selectedMsRef.current = ms;
      setSelectedDateState(toDateStr(ms));
    } else {
      selectedMsRef.current = 0;
      setSelectedDateState('');
    }
  }, [startDate]);

  const clamp = useCallback((ms: number): number => {
    const start = toMs(startDate);
    const end = toMs(endDate);
    if (start && ms < start) return start;
    if (end && ms > end) return end;
    return ms;
  }, [startDate, endDate]);

  // RAF animation loop
  useEffect(() => {
    if (!playing) {
      if (rafHandleRef.current !== null) {
        cancelAnimationFrame(rafHandleRef.current);
        rafHandleRef.current = null;
      }
      lastFrameTimeRef.current = null;
      return;
    }

    function frame(timestamp: number) {
      if (!playingRef.current) return;

      if (lastFrameTimeRef.current === null) {
        lastFrameTimeRef.current = timestamp;
      }

      const elapsed = timestamp - lastFrameTimeRef.current;

      if (elapsed >= FRAME_INTERVAL_MS) {
        lastFrameTimeRef.current = timestamp;
        const advance = speedRef.current * DAY_MS;
        const nextMs = selectedMsRef.current + advance;
        const end = toMs(endDate);

        if (end && nextMs >= end) {
          // Reached end — stop
          selectedMsRef.current = end;
          setSelectedDateState(toDateStr(end));
          setPlaying(false);
          playingRef.current = false;
          return;
        }

        selectedMsRef.current = nextMs;
        setSelectedDateState(toDateStr(nextMs));
      }

      rafHandleRef.current = requestAnimationFrame(frame);
    }

    rafHandleRef.current = requestAnimationFrame(frame);

    return () => {
      if (rafHandleRef.current !== null) {
        cancelAnimationFrame(rafHandleRef.current);
        rafHandleRef.current = null;
      }
    };
  }, [playing, endDate]);

  const play = useCallback(() => {
    if (!startDate || !endDate) return;
    setPlaying(true);
  }, [startDate, endDate]);

  const pause = useCallback(() => {
    setPlaying(false);
  }, []);

  const stepForward = useCallback(() => {
    const nextMs = clamp(selectedMsRef.current + DAY_MS);
    selectedMsRef.current = nextMs;
    setSelectedDateState(toDateStr(nextMs));
  }, [clamp]);

  const stepBack = useCallback(() => {
    const nextMs = clamp(selectedMsRef.current - DAY_MS);
    selectedMsRef.current = nextMs;
    setSelectedDateState(toDateStr(nextMs));
  }, [clamp]);

  const setSpeed = useCallback((s: AnimationSpeed) => {
    speedRef.current = s;
    setSpeedState(s);
  }, []);

  const setDate = useCallback((date: string) => {
    const ms = clamp(toMs(date));
    selectedMsRef.current = ms;
    setSelectedDateState(toDateStr(ms));
  }, [clamp]);

  return { selectedDate, playing, speed, play, pause, stepForward, stepBack, setSpeed, setDate };
}
